import { createFileRoute } from '@tanstack/react-router';
import { useGetQuestionsQuery } from '@/api/questions/getQuestions';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { Title } from '@/components/composite/Title';
import { BackButton } from '@/components/composite/buttons/BackButton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye } from 'lucide-react';
import { Formik, Form, useFormikContext } from 'formik';
import { useState, useEffect, useRef } from 'react';
import PreviewModal from '@/components/composite/PreviewModal';
import { useFieldTranslation } from '@/hooks/useFieldTranslation';
import { useQuestionForm } from '@/hooks/forms/useQuestionForm';
import { QuestionFieldsSection } from '@/components/forms/question/QuestionFieldsSection';
import { QuestionOptionsSection } from '@/components/forms/question/QuestionOptionsSection';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';

export const Route = createFileRoute('/dashboard/questions/$id')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      pickem_id: search.pickem_id as string | undefined,
    };
  },
  component: RouteComponent,
});

function TiebreakerOptionsClearer() {
  const { values, setFieldValue } = useFormikContext<QuestionFormValues>();
  const prevQuestionTypeRef = useRef(values.question_type);

  useEffect(() => {
    const currentType = values.question_type;
    const prevType = prevQuestionTypeRef.current;

    if (currentType === 'tiebreaker' && prevType !== 'tiebreaker' && values.options.length > 0) {
      console.log('🔄 Clearing options for tiebreaker question');
      setFieldValue('options', []);
    }

    prevQuestionTypeRef.current = currentType;
  }, [values.question_type, values.options.length, setFieldValue]);

  return null;
}

export function RouteComponent() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const { data: questions = [], isLoading: questionsLoading } = useGetQuestionsQuery();
  const { data: pickems = [], isLoading: pickemsLoading } = useGetPickemsQuery();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<QuestionFormValues | null>(null);
  const { translateField, isFieldTranslating } = useFieldTranslation();

  const isNewQuestion = id === 'new';
  const question = isNewQuestion ? undefined : questions.find(q => String(q.question_id) === id);

  // Always allow editing questions regardless of pickem status
  const isQuestionEditable = true;
  const questionEditMessage = question?.edit_message;

  const form = useQuestionForm({
    question: question as any,
    questionId: id,
    searchPickemId: search.pickem_id,
    isNewQuestion,
  });

  if (questionsLoading || pickemsLoading) {
    return (
      <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <Title>Loading...</Title>
            <BackButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-muted-foreground">Loading question data...</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-4 w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <Title>{isNewQuestion ? 'Add new' : 'Edit'} question</Title>
          <BackButton />
        </div>
      </CardHeader>

      <CardContent>
        <Formik
          initialValues={form.initialValues}
          validationSchema={form.validationSchema}
          onSubmit={async values => {
            console.log('=== Question Form Submit ===');
            console.log('Form Values:', JSON.stringify(values, null, 2));
            console.log('Question Type:', values.question_type);
            console.log('Options Count:', values.options?.length || 0);

            try {
              await form.validationSchema.validate(values, { abortEarly: false });
              console.log('Validation: PASSED ✓');
            } catch (err: any) {
              console.log('Validation: FAILED ✗');
              console.log('Validation Errors:', err.errors);
              return;
            }

            console.log('Calling handleSubmit...');
            form.handleSubmit(values);
          }}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
        >
          {formik => {
            console.log('Formik Render - Errors:', formik.errors);
            if (formik.errors.options) {
              console.log('Options Errors Detail:', formik.errors.options);
            }
            console.log('Formik Render - Values:', formik.values);
            console.log('Formik Render - Touched:', formik.touched);
            console.log('Formik Render - isValid:', formik.isValid);
            console.log('Formik Render - isSubmitting:', formik.isSubmitting);

            return (
              <Form className="flex flex-col gap-6">
                <TiebreakerOptionsClearer />

                <QuestionFieldsSection
                  formik={formik}
                  pickems={pickems}
                  translateField={translateField}
                  isFieldTranslating={isFieldTranslating}
                  isEditable={isQuestionEditable}
                  isNewQuestion={isNewQuestion}
                />

                <QuestionOptionsSection
                  formik={formik}
                  translateField={translateField}
                  isFieldTranslating={isFieldTranslating}
                  isEditable={isQuestionEditable}
                />

                {/* Info message for non-editable questions */}
                {!isQuestionEditable && questionEditMessage && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-blue-800">
                      {questionEditMessage}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    data-test-id="question-preview-button"
                    variant="outline"
                    onClick={() => {
                      setPreviewData(formik.values);
                      setPreviewOpen(true);
                    }}
                    className="flex-1"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Preview
                  </Button>
                  {isQuestionEditable && (
                    <Button
                      type="submit"
                      data-test-id="question-submit-button"
                      disabled={form.isPending}
                      className="flex-1"
                    >
                      {form.isPending ? 'Saving...' : 'Save Question'}
                    </Button>
                  )}
                </div>
              </Form>
            );
          }}
        </Formik>

        {previewData && (
          <PreviewModal
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            questionType={previewData.question_type || 'team'}
            questionText={previewData.question_text_en}
            questionImage={previewData.question_image_en}
            options={previewData.options}
            language="en"
          />
        )}
      </CardContent>
    </Card>
  );
}
